import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Old declarative way which put onus on reference fixup to correctly parse/walk nested lval metadata to arbitrary depth
// Above ReferenceFixupContext contract much better as each 'layer' in the walk handles the layer-specific logic.
// export type ObjectLValType = { obj: Object; propName: string | number };
// export type ArrayLValType = { arrayHolder?: ObjectLValType, array: Array<Object | null>; index: number }
// export type MapLValType = { mapHolder?: ObjectLValType, map: Array<Object | null>; propName: string }
// export type LValType = ObjectLValType | ArrayLValType | MapLValType

// Internal types

// identify lval (either an obj.prop or an array[index], either suitable for LHS of an assignment)

export class DeserializeContext {
  constructor() {
    _defineProperty(this, "objMap", {});
    _defineProperty(this, "refMap", {});
  }
  static createFieldReferenceContext(backingObj, propName) {
    return new FieldReferenceFixup(backingObj, propName);
  }
  static createContainerReferenceContext(backingObj, propName,
  // string for map lookups, and number for array lookups
  /* array or map in json payload*/
  defaultJsonContainer) {
    return new ContainerReferenceFixup(backingObj, propName, defaultJsonContainer);
  }
  /**
   * Return obj with given objId if it has already been encountered in the deserialization logic,
   * otherwise return undefined.  Note that null is is a valid value for a deserialized obj, but undefined it not.
   */
  getObject(objId) {
    return this.objMap[objId];
  }

  /**
   * Register given object by objId.  Note that the object is typically registered before it is fully constructed.
   */
  registerObject(objId, obj) {
    this.objMap[objId] = obj;
    const fixupRefs = this.refMap[objId];
    if (fixupRefs) {
      this.fixupReferences(obj, fixupRefs);
      // can remove as now that objId is in map, there won't be any more addReference calls for it
      delete this.refMap[objId];
    }
  }

  /**
   * As refId is not yet defined but is needed to set the obj[propName] value, record it so that the reference
   * can be added later after the obj with refId has been defined.
   */
  addReference(refId, targetLVal) {
    const fixupRefs = this.refMap[refId];
    if (fixupRefs) {
      fixupRefs.push(targetLVal);
    } else {
      this.refMap[refId] = [targetLVal];
    }
  }

  /**
   * Process the references (which were added via addReference) to fixup the object properties (obj[propName])
   * with the (now) resolvable objects that have the given objId.  Thus does: obj[propName] = lookup(refId).
   * Should be called once after entire tsog stream has been processed.
   */
  fixupReferences(refObj, refInjectors) {
    refInjectors.forEach(refInjector => refInjector.injectReferenceObj(refObj));
  }
}
// Implementing classes of ReferenceFixupContext.
// Do NOT export, instead use DeserializeContext.create$RFCName factory methods

class FieldReferenceFixup {
  constructor(backingObj, propName) {
    this.backingObj = backingObj;
    this.propName = propName;
  }
  getBackingContainer() {
    return this.backingObj[this.propName];
  }
  injectReferenceObj(obj) {
    this.backingObj[this.propName] = obj;
  }
}
class ContainerReferenceFixup {
  // Backing object will be undefined if decoding a 'top-level' container that does not (transitively) belong to an object
  constructor(backingObj, propName,
  // string for map lookups, and number for array lookups
  /* array or map in json payload*/
  defaultJsonContainer) {
    this.backingObj = backingObj;
    this.propName = propName;
    this.defaultJsonContainer = defaultJsonContainer;
  }
  getBackingContainer() {
    // This is invoked by nested references further down the chain, and NOT by this method's injectReferenceObj.
    // It is ONLY evaluated when it is invoked (can not be evaluated in constructor).
    const underlyingBackingContainer = this.backingObj?.getBackingContainer();
    return underlyingBackingContainer ? underlyingBackingContainer[this.propName] : undefined;
  }
  injectReferenceObj(obj) {
    const backingObj = this.backingObj?.getBackingContainer() ?? this.defaultJsonContainer;
    backingObj[this.propName] = obj;
  }
}
//# sourceMappingURL=DeserializeContext.js.map
